import type { ReactNode } from 'react';
import { Button, type ButtonSize } from '@viquard/doc-ui/components';
import type { CalendarKindMap } from '../types.js';
import { useCalendarMessages } from '../i18n.js';

export interface MultiKindFilterProps {
  readonly kindTheme: CalendarKindMap;
  /** Selected kinds. Empty set ⇒ "All kinds". */
  readonly value: ReadonlySet<string>;
  readonly onChange: (next: Set<string>) => void;
  readonly visibleKinds?: ReadonlySet<string> | undefined;
  readonly allLabel?: string | undefined;
  /** Per-chip Button size. Default `sm` (compact desktop strip).
   *  FilterBar passes `md` on mobile so each chip clears the WCAG /
   *  iOS HIG 44 px touch target. */
  readonly size?: ButtonSize | undefined;
}

/**
 * Multi-select kind filter — DEMO/calendar.jsx form: a horizontal chip
 * strip, one chip per kind. Built entirely on doc-ui `Button`.
 *
 *   - "All kinds" chip → primary (active) when no selection, secondary
 *     (inactive) when any kind is filtered. Clicking clears the set.
 *   - Each kind chip → primary (active) when in the set, secondary
 *     (inactive) otherwise. Clicking toggles. The leading dot uses
 *     the kind's accent so users can scan to a kind by colour.
 *
 * Wraps on narrow containers so every option stays discoverable on a
 * 380 px phone — no horizontal scroll, no hidden chevron.
 */
export function MultiKindFilter(props: MultiKindFilterProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    kindTheme,
    value,
    onChange,
    visibleKinds,
    allLabel = messages.allKinds,
    size = 'sm',
  } = props;

  const entries = Object.entries(kindTheme).filter(([k]) => k !== 'default');
  const allActive = value.size === 0;

  const toggle = (kind: string): void => {
    const next = new Set(value);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    onChange(next);
  };
  const clearAll = (): void => { onChange(new Set()); };

  return (
    <div className="vq-cal__kind-strip" role="group" aria-label="Filter by kind">
      <Button
        variant={allActive ? 'primary' : 'secondary'}
        size={size}
        shape="pill"
        onClick={clearAll}
      >
        {allLabel}
      </Button>
      {entries.map(([kind, def]) => {
        if (visibleKinds !== undefined && !visibleKinds.has(kind)) return null;
        const active = value.has(kind);
        return (
          <Button
            key={kind}
            variant={active ? 'primary' : 'secondary'}
            size={size}
            shape="pill"
            iconLeft={
              <span
                className="vq-cal__kind-dot"
                style={{ background: def.color }}
                aria-hidden="true"
              />
            }
            onClick={() => { toggle(kind); }}
          >
            {def.label}
          </Button>
        );
      })}
    </div>
  );
}
